import React from 'react';
import Box from '@mui/material/Box';
import { DynamicCarouselSlideItem } from './DynamicCarousel';
import Button from '@mui/material/Button';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { shopifyCDNFile } from 'lib/shopifyImageSizer';
import Stack from '@mui/material/Stack';
import SplitPageTitle from 'components/Layout/SplitPageTitle';
import Container from 'components/Layout/Container';
import { SxProps } from '@mui/system';
import { Theme } from '@mui/material/styles';
import debug from 'debug';
import { useHistory } from 'react-router-dom';
import { HashLink as RouterLink } from 'react-router-hash-link';
import CardTilt from 'components/Product/CardTilt';
const d = debug('DynamicCarouselSlide');

interface DynamicCarouselSlideProps {
  item: DynamicCarouselSlideItem;
  children?: any;
  sx?: SxProps<Theme>;
  on?: boolean;
}

interface InvisibleBoxProps {
  visible?: boolean;
  children?: any;
}

/**
 * Helper to render things invisible.. (yet maintain dom position for layout etc)
 */
const InvisibleBox: React.FC<InvisibleBoxProps> = ({ visible = true, children }) => {
  if (!children) return null;
  return (
    <Box
      sx={{
        opacity: visible ? 1 : 0,
        transition: 'all 0.5s ease-in-out',
      }}
    >
      {children}
    </Box>
  );
};

interface ExplodeBoxProps {
  sx?: SxProps<Theme>;
  children?: any;
}

/**
 * Helper to explode things past the width of the carousel... but contain them to
 */
const ExplodeBox: React.FC<ExplodeBoxProps> = ({ sx, children }) => {
  if (!children) return null;
  return (
    <Box
      sx={{
        ...sx,
        width: '100vw',
      }}
    >
      <Container>{children}</Container>
    </Box>
  );
};

const DynamicCarouselSlide: React.FC<DynamicCarouselSlideProps> = ({ item, on = true, sx, children }) => {
  const history = useHistory();
  const { options } = item;
  d(item);
  let backgroundImage = options?.ignoreBackground ? undefined : item?.backgroundImage;
  if (backgroundImage && !backgroundImage.includes('http')) {
    backgroundImage = shopifyCDNFile(backgroundImage);
  }
  let bodyImage = item?.bodyImage;
  if (item.bodyImage && !item.bodyImage.includes('http')) {
    bodyImage = shopifyCDNFile(item.bodyImage);
  }
  const backgroundStyle = item.bright ? `url(${backgroundImage})` : `linear-gradient(180deg, rgba(17, 17, 17, 0.3), rgba(17, 17, 17, 0.5)), url(${backgroundImage})`;
  const onClick = () => {
    if (item.buttonTo) history.push(item.buttonTo);
  };
  return (
    <Box
      sx={{
        pt: 12, // Works for mobile to desktop to clear the navs etc.
        pb: 4, // Clears the indicators
        display: 'flex',
        flexFlow: 'column',
        justifyContent: 'center',
        height: '100%', // Grow to the largest slide!
        minHeight: '80vh', // Be at least 80% of the screen height - can grow larger!
        backgroundSize: 'cover',
        backgroundPosition: 'center center',
        backgroundImage: backgroundImage ? backgroundStyle : undefined,
        ...sx,
      }}
    >
      <Stack alignItems="center" spacing={3}>
        {item.title && !item.subtitle && (
          <InvisibleBox visible={on}>
            <ExplodeBox sx={{ typography: 'h1', maxWidth: 800 }}>{item.title}</ExplodeBox>
          </InvisibleBox>
        )}
        {item.title && item.subtitle && (
          <InvisibleBox visible={on && !options?.ignoreTitles}>
            <ExplodeBox>
              <SplitPageTitle fullWidth title={item.title} subtitle={item.subtitle} sx={{ textAlign: 'left' }} />
            </ExplodeBox>
          </InvisibleBox>
        )}
        {item.bodyImage && (
          <Box sx={{ maxWidth: 550 }}>
            <CardTilt enabled={on}>
              <img src={bodyImage} alt={item.bodyImage} />
            </CardTilt>
          </Box>
        )}
        <CardTilt enabled={on}>{item.bodyElement}</CardTilt>
        {item.body && <Box sx={{ typography: 'body1', maxWidth: 500 }}>{item.body}</Box>}
        {children}
        <InvisibleBox visible={on}>
          <ExplodeBox>
            {item.buttonUseClick && item.buttonLabel && (
              <Button size="small" variant="outlined" color="secondary" onClick={onClick} endIcon={<ArrowForwardIcon />}>
                {item.buttonLabel}
              </Button>
            )}
            {!item.buttonUseClick && item.buttonLabel && item.buttonTo && (
              <Button size="small" variant="outlined" color="secondary" component={RouterLink} to={item.buttonTo} endIcon={<ArrowForwardIcon />}>
                {item.buttonLabel}
              </Button>
            )}
          </ExplodeBox>
        </InvisibleBox>
        <InvisibleBox visible={on}>{item.caption && <ExplodeBox sx={{ typography: 'body1', minHeight: 38 }}>{item.caption}</ExplodeBox>}</InvisibleBox>
      </Stack>
    </Box>
  );
};

export default DynamicCarouselSlide;
